export const ROLES = {
  USER: "user",
  CCA: "cca",
  CCA_TL: "cca_tl", // team leader
  CCA_TR: "cca_tr", // trainer
  CCA_SNR: "cca_snr", //senior
  TRADING: "trading",
  TRADING_FO: "trading_fo",
  TRADING_MO: "trading_mo",
  PAYMENTS: "payments",
  PAYMENTS_EXP: "payments_experiments",
  BETA_TESTER: "beta_tester",
  BILLING: "billing",
  METERING: "metering",
  COMMERCIAL_METERING: "commercial_metering",
  DEBT_MANAGEMENT: "debt_management",
  DCA: "dca",
  MARKETING: "marketing",
  DEVELOPER: "developer",
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

export type Permission = string

export interface IPermissionService {
  hasPermission(permission: Permission, roles: Role[]): boolean

  hasAllPermissions(permissions: Permission[], roles: Role[]): boolean

  hasAnyPermission(permissions: Permission[], roles: Role[]): boolean
}

const rolesToPermissions: Record<Role, Permission[]> = {
  user: [],
  cca: ["agent.customer", "admin.users", "agent.imbpBookBasicJobs"],
  cca_tl: [
    "agent.customer",
    "admin.users",
    "admin.referrals",
    "admin.newsfeed",
    "agent.imbpBookInvestigatoryJobs",
  ],
  cca_tr: [
    "agent.customer",
    "admin.users",
    "admin.referrals",
    "admin.synchronisedAccounts",
    "agent.imbpBookInvestigatoryJobs",
  ],
  cca_snr: [
    "agent.customer",
    "admin.users",
    "admin.referrals",
    "agent.imbpBookInvestigatoryJobs",
  ],
  trading: [
    "agent.customer",
    "admin.users",
    "admin.energyProducts",
    "admin.featuresAdmin",
    "admin.trades",
  ],
  trading_fo: [
    "agent.customer",
    "admin.users",
    "admin.energyProducts",
    "admin.featuresAdmin",
    "admin.trades",
  ],
  trading_mo: [
    "agent.customer",
    "admin.users",
    "admin.energyProducts",
    "admin.featuresAdmin",
    "admin.trades",
  ],
  payments: [
    "agent.customer",
    "admin.users",
    "admin.directDebitReviews",
    "agent.imbpBookEmergencyJobs",
    "admin.depositRequests",
    "agent.warrants.remove",
    "agent.warrants.meterExchange",
    "agent.meterReadEstimator.toggleSeasonality",
    "agent.meterReadEstimator.submitSystemRead",
  ],
  payments_experiments: [],
  beta_tester: ["admin.smartCharging"],
  billing: [
    "agent.customer",
    "admin.users",
    "admin.billing",
    "agent.imbpBookEmergencyJobs",
    // Meter Read Estimator (SO-32409): only Metering, Billing and Payments may switch
    // seasonality off or write an estimate to Junifer. The gateway checks the
    // same roles, so this hides the controls rather than being the only gate.
    "agent.meterReadEstimator.toggleSeasonality",
    "agent.meterReadEstimator.submitSystemRead",
  ],
  metering: [
    "agent.customer",
    "admin.users",
    "admin.smartMeters",
    "agent.imbpBookEmergencyJobs",
    "agent.customer.showEngineerDetails",
    "agent.meterReadEstimator.toggleSeasonality",
    "agent.meterReadEstimator.submitSystemRead",
  ],
  commercial_metering: [
    "agent.customer",
    "admin.users",
    "admin.smartMeters",
    "agent.imbpBookEmergencyJobs",
    "agent.customer.showEngineerDetails",
  ],
  debt_management: [
    "agent.customer",
    "admin.users",
    // The DCA allocation screen, /operations/dca-management (SO-32338). This role is
    // the only grant, so removing it here makes the page unreachable rather than
    // merely hiding it from the nav.
    "admin.debtCollection",
  ],
  dca: ["agent.customer"],
  marketing: ["admin.loyalty", "admin.marketingBanners"],
  developer: ["*"],
}

class PermissionsServiceClass implements IPermissionService {
  hasPermission(permission: Permission, roles: Role[]): boolean {
    const userPermissions = this.getUserPermissions(roles)
    return userPermissions.includes(permission) || userPermissions.includes("*")
  }

  hasAllPermissions(permissions: Permission[], roles: Role[]): boolean {
    return permissions.every((permission) =>
      this.hasPermission(permission, roles),
    )
  }

  hasAnyPermission(permissions: Permission[], roles: Role[]): boolean {
    return permissions.some((permission) =>
      this.hasPermission(permission, roles),
    )
  }

  hasCustSearchPermission(roles: Role[]): boolean {
    const userPermissions = this.getUserPermissions(roles)

    return (
      userPermissions.includes("agent.customer") ||
      userPermissions.includes("*")
    )
  }

  private mapRolesToPermissions(roles: Role[]): Permission[] {
    const permissions = roles.flatMap((role) => {
      if (role in rolesToPermissions) {
        return rolesToPermissions[role]
      }
      return []
    })
    return Array.from(new Set(permissions))
  }

  private getUserPermissions(roles: Role[]) {
    return this.mapRolesToPermissions(roles)
  }
}

export const PermissionsService = new PermissionsServiceClass()
